import truckDriverPng from '../../assets/truck_driver.png';

const DEFAULT_VEHICLE_IMAGE =
  'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=200&h=140&fit=crop&q=80';

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

/**
 * @param {string|undefined} iso
 * @returns {string}
 */
export function formatTelematicsDateTime(iso) {
  if (!iso) return '—';
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return String(iso);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return String(iso);
  }
}

/**
 * @param {number|null|undefined} degrees
 * @returns {string}
 */
export function headingToCompassLabel(degrees) {
  if (degrees == null || Number.isNaN(Number(degrees))) return '—';
  const d = ((Number(degrees) % 360) + 360) % 360;
  const index = Math.round(d / 45) % 8;
  return COMPASS[index];
}

/**
 * @param {Array<Object>|undefined} extras
 * @param {string} name
 * @returns {Object|undefined}
 */
function findParsedExtra(extras, name) {
  if (!Array.isArray(extras)) return undefined;
  return extras.find(e => e?.name === name);
}

/**
 * Normalizes telematics `last_position` to numeric lat/lng for the map.
 * API may use `lat`/`lng` or `latitude`/`longitude` (often strings).
 * @param {Object|null|undefined} pos
 * @returns {{ lat: number|null, lng: number|null, has_position: boolean, speed_kmh: *, heading: * }}
 */
export function parseTelematicsLastPosition(pos) {
  if (!pos) {
    return {
      lat: null,
      lng: null,
      has_position: false,
      speed_kmh: null,
      heading: null,
    };
  }

  const latRaw = pos?.lat ?? pos?.latitude;
  const lngRaw = pos?.lng ?? pos?.longitude;
  const lat = latRaw != null ? Number(latRaw) : null;
  const lng = lngRaw != null ? Number(lngRaw) : null;
  const has_position =
    lat != null &&
    lng != null &&
    !Number.isNaN(lat) &&
    !Number.isNaN(lng);

  return {
    lat: has_position ? lat : null,
    lng: has_position ? lng : null,
    has_position,
    speed_kmh: pos?.speed_kmh ?? pos?.speed ?? null,
    heading: pos?.heading ?? null,
  };
}

/**
 * Maps a list item from GET /telematics/devices to dashboard/list shape.
 * @param {Object} device
 * @returns {Object}
 */
export function mapDeviceListItemToPatrol(device) {
  const terminalPhone = device?.terminal_phone ?? device?.id;
  const { lat, lng, has_position, speed_kmh, heading } = parseTelematicsLastPosition(
    device?.last_position
  );

  return {
    id: terminalPhone,
    patrol_id: terminalPhone,
    terminal_phone: terminalPhone,
    vehicle_id: device?.vehicle_id,
    license_plate: device?.license_plate ?? '—',
    name: device?.license_plate ?? terminalPhone ?? '—',
    last_connected_at: formatTelematicsDateTime(device?.last_connected_at),
    last_connected_at_raw: device?.last_connected_at,
    lat,
    lng,
    has_position,
    speed_kmh,
    heading,
    image_url: truckDriverPng,
    detail_image_url: DEFAULT_VEHICLE_IMAGE,
  };
}

/**
 * Maps GET /telematics/devices/{terminal_phone} response to VehicleInfoPanel shape.
 * @param {Object} detail
 * @param {Object} [listPatrol]
 * @returns {Object}
 */
/**
 * Formats seconds as a compact duration (e.g. `1h 5m`, `13 min`).
 * @param {number|null|undefined} seconds
 * @returns {string}
 */
export function formatDurationSeconds(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const total = Math.max(0, Math.round(Number(seconds)));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes > 0) return `${minutes} min`;
  return `${secs}s`;
}

/**
 * @param {number|null|undefined} score
 * @returns {string}
 */
export function driverScoreToGrade(score) {
  if (score == null || Number.isNaN(Number(score))) return '—';
  const n = Number(score);
  if (n >= 90) return 'A';
  if (n >= 80) return 'B';
  if (n >= 70) return 'C';
  if (n >= 60) return 'D';
  return 'F';
}

/**
 * Maps GET /telematics/devices/{terminal_phone}/metrics to VehicleInfoPanel KPI fields.
 * @param {Object} metricsResponse
 * @returns {Object}
 */
export function mapTelematicsMetricsToVehicle(metricsResponse) {
  const metrics = metricsResponse?.metrics ?? {};
  const window = metricsResponse?.window ?? {};
  const routePoints = Array.isArray(metrics.route) ? metrics.route : [];
  const stopList = Array.isArray(metrics.stops) ? metrics.stops : [];

  const windowSeconds =
    window.from && window.to
      ? (new Date(window.to).getTime() - new Date(window.from).getTime()) / 1000
      : null;

  const stops = stopList.map((stop, index) => {
    const lat = stop?.lat;
    const lng = stop?.lng;
    const coordLabel =
      lat != null && lng != null
        ? `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`
        : null;
    return {
      name: stop?.name ?? coordLabel ?? `Stop ${index + 1}`,
      arrivedAt: formatTelematicsDateTime(stop?.started_at),
      dwell: formatDurationSeconds(stop?.dwell_seconds),
    };
  });

  const routeSummary =
    routePoints.length > 0
      ? `${routePoints.length} GPS points · ${formatTelematicsDateTime(window.from)} – ${formatTelematicsDateTime(window.to)}`
      : 'No route points in this window';

  return {
    distance_km: metrics.distance_km ?? null,
    distance_travelled_km: metrics.distance_km ?? null,
    trip_count: metrics.trip_count ?? null,
    idling_time: formatDurationSeconds(metrics.idling_seconds),
    engine_on_hours:
      metrics.engine_on_hours != null
        ? Number(metrics.engine_on_hours)
        : metrics.engine_on_seconds != null
          ? Number(metrics.engine_on_seconds) / 3600
          : null,
    harsh_acceleration_count: metrics.harsh_acceleration_count ?? null,
    harsh_braking_count: metrics.harsh_braking_count ?? null,
    harsh_cornering_count: metrics.harsh_cornering_count ?? null,
    overspeed_events_count: metrics.overspeed_event_count ?? null,
    avg_speed_kmh: metrics.speed_kmh?.avg ?? null,
    max_speed_kmh: metrics.speed_kmh?.max ?? null,
    speed_85th_kmh: metrics.speed_kmh?.p85 ?? null,
    driver_score:
      metrics.driver_score != null
        ? Number(metrics.driver_score).toFixed(1)
        : null,
    driver_grade: driverScoreToGrade(metrics.driver_score),
    stops,
    trips: [],
    route_summary: routeSummary,
    route_distance:
      metrics.distance_km != null
        ? `${Number(metrics.distance_km).toLocaleString(undefined, { maximumFractionDigits: 1 })} km`
        : null,
    route_duration: formatDurationSeconds(windowSeconds),
    route_points: routePoints,
    metrics_loaded: true,
    metrics_window: window,
  };
}

export function mapTelematicsDetailToVehicle(detail, listPatrol = null) {
  const device = detail?.device ?? {};
  const connectivity = detail?.connectivity ?? {};
  const pos = detail?.last_position;
  const {
    lat,
    lng,
    has_position,
    speed_kmh: posSpeedKmh,
    heading: posHeading,
  } = parseTelematicsLastPosition(pos);
  const latestEvent = detail?.recent_events?.[0];
  const parsed = latestEvent?.parsed;
  const extras = parsed?.extras ?? [];

  const mileageExtra = findParsedExtra(extras, 'mileage');
  const fuelExtra = findParsedExtra(extras, 'fuel');
  const recorderExtra = findParsedExtra(extras, 'recorder_speed');

  const isWorking =
    connectivity?.receiving_data === true || connectivity?.status === 'ok';

  const heading = posHeading ?? pos?.heading ?? parsed?.heading;
  const terminalPhone =
    detail?.terminal_phone ??
    device?.terminal_phone ??
    listPatrol?.terminal_phone;

  return {
    id: terminalPhone,
    patrol_id: terminalPhone,
    terminal_phone: terminalPhone,
    vehicle_id: device?.vehicle_id ?? listPatrol?.vehicle_id,
    license_plate: device?.license_plate ?? listPatrol?.license_plate ?? '—',
    licensePlate: device?.license_plate ?? listPatrol?.license_plate ?? '—',
    lat,
    lng,
    has_position,
    status: isWorking ? 'WORKING' : 'OFFLINE',
    tracker_serial:
      latestEvent?.serial != null ? String(latestEvent.serial) : null,
    last_connected_at: formatTelematicsDateTime(device?.last_connected_at),
    last_connected_time: formatTelematicsDateTime(device?.last_connected_at),
    speed_kmh:
      posSpeedKmh ??
      pos?.speed_kmh ??
      pos?.speed ??
      parsed?.speed_kmh ??
      listPatrol?.speed_kmh ??
      null,
    direction_degrees: heading ?? null,
    direction_label: headingToCompassLabel(heading),
    mileage_km: mileageExtra?.odometer_km ?? mileageExtra?.mileage ?? null,
    fuel_liters: fuelExtra?.liters ?? null,
    fuel_percent: null,
    altitude_m: pos?.altitude_m ?? parsed?.altitude_m ?? null,
    recorded_at: formatTelematicsDateTime(
      pos?.recorded_at ?? pos?.updated_at ?? latestEvent?.stored_at
    ),
    recorder_speed_kmh: recorderExtra?.speed_kmh ?? null,
    image_url: listPatrol?.image_url ?? truckDriverPng,
    detail_image_url: listPatrol?.detail_image_url ?? DEFAULT_VEHICLE_IMAGE,
    connectivity_summary: connectivity?.summary ?? null,
  };
}

/**
 * Maps GET /telematics/devices/{terminal_phone}/report to LiveVehicleReport list row.
 * @param {Object} report
 * @returns {Object}
 */
export function mapTrackerReportToListItem(report) {
  const terminalPhone =
    report?.terminal_phone_resolved ?? report?.terminal_phone ?? '';
  const vehicle = report?.vehicle ?? {};
  const device = report?.device ?? {};
  const kpis = report?.kpis ?? {};
  const speed = kpis?.speed_kmh ?? {};
  const pdf = report?.pdf ?? {};

  const licensePlate =
    vehicle?.registration_no ??
    device?.license_plate ??
    '—';
  const vehicleLabel =
    vehicle?.model_name?.trim() ||
    licensePlate ||
    terminalPhone ||
    '—';

  const pdfKey = typeof pdf?.key === 'string' ? pdf.key : '';
  const fileName = pdfKey
    ? pdfKey.split('/').pop()
    : `Live_Vehicle_Report_${String(licensePlate).replace(/\s+/g, '')}.pdf`;

  return {
    id: terminalPhone || report?.vehicle?.vehicle_id || fileName,
    file_name: fileName,
    vehicle_name: vehicleLabel,
    license_plate: licensePlate,
    tracker_serial: terminalPhone,
    distance_km: kpis?.distance_km ?? null,
    avg_speed_kmh: speed?.avg ?? null,
    trip_count: kpis?.trip_count ?? null,
    driver_score: kpis?.driver_score ?? null,
    events_analyzed: report?.window?.events_analyzed ?? null,
    pdf_download_url: pdf?.download_url ?? null,
    window_from: report?.window?.from,
    window_to: report?.window?.to,
  };
}
